import type { HsCamera } from '../../engine-new/objects/cameras/camera.class';
import { HsToolsConstants } from '../tools-constants.constants';
import type { HsCameraTool } from './camera-tool.interface';
import { HsControlActionType } from '../actions/control-action-type.enum';
import type { HsControlAction } from '../actions/control-action.abstract';
import type { HsCanvasSize } from '../utils/canvas-size.class';
import type { HsVector2 } from '../../math-new/objects/vector2.class';
import { HsScrollControlAction } from '../actions/scroll-control-action.class';
import { HsZoomControlAction } from '../actions/zoom-control-action.class';
import { hsComputeZoomTravel } from './zoom-travel.function';
import { HsMouseMoveControlAction } from '../actions/mouse-move-control-action.class';
import { HsKeyboardControlAction } from '../actions/keyboard-control-action.class';
import { HsVector3 } from '../../math-new/objects/vector3.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import { hsApplyTransformationToCamera } from './apply-transformation-to-camera.function';
import { HsQuaternion } from '../../math-new/objects/quaternion.class';
import { HsHypersceneConstants } from '../../math-new/constants/hyperscene-constants.global';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';
import { HsHypersceneMathUtils } from '../../math-new/functions/hyperscene-math-utils.class';
import { hsOrbitCameraMatrix } from './camera-views.function';

const SPEED_MOVE: number = 0.3;
const SPEED_ROTATE: number = 0.5;
const SCROLL_DISTANCE_FACTOR: number = 1 / 3;
const COEFFICIENT_DELTA_TIME_KEYBOARD: number = 0.1;
const SPEED_CHANGE: number = 0.05;
const MIN_SPEED: number = 0.05;
const MAX_DELTA_TIME_MS: number = 32;
/** Orbit speed in degrees per CSS pixel of drag. */
const ORBIT_DEGREES_PER_PIXEL: number = 0.4;

/** What a drag does with `HsCameraDefaultMove`: turn the camera in place, orbit around a point, or pan. */
export type HsDragNavigation = 'look' | 'orbit' | 'pan';

/**
 * Drag actions to navigation, as CAD and BIM viewers map the mouse buttons: the left button looks around from the
 * camera's place, the right one orbits around the pressed point, the middle one pans. A one-finger touch drag sends
 * `RightMove`, so it orbits; a two-finger drag sends `MiddleMove`.
 */
export const HS_DEFAULT_DRAG_NAVIGATION: Readonly<Partial<Record<HsControlActionType, HsDragNavigation>>> = {
  [HsControlActionType.LeftMove]: 'look',
  [HsControlActionType.MiddleMove]: 'pan',
  [HsControlActionType.RightMove]: 'orbit',
};

export class HsCameraDefaultMove implements HsCameraTool {
  /**
   * The point an orbit drag turns around; null looks around from the camera's place instead. Hosts usually set it
   * on each press to the model point under the cursor.
   */
  public orbitPivot: HsVector3 | null = null;

  /**
   * The point a pan drag keeps under the cursor: the pan moves by the world size of a pixel at its depth, so it
   * suits survey-scale and room-scale scenes alike. Null pans by a fixed world step per pixel, as touch pans that
   * scale their deltas themselves expect. Hosts usually set it on a middle press and clear it on release.
   */
  public panAnchor: HsVector3 | null = null;

  /** What each drag action does; replace entries to remap the buttons. */
  public dragNavigation: Partial<Record<HsControlActionType, HsDragNavigation>> = { ...HS_DEFAULT_DRAG_NAVIGATION };

  private speed: number = HsToolsConstants.SPEED_FACTOR;

  private frameStartTime: number | null = null;

  private frameEndTime: number | null = null;

  private deltaTime: number = 1;

  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp3: HsVector3 = HsVector3.makeZeroVector();

  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #matrix4Temp2: HsMatrix4 = HsMatrix4.makeIdentityMatrix();

  static readonly #quaternionTemp1: HsQuaternion = HsQuaternion.makeIdentityQuaternion();
  static readonly #quaternionTemp2: HsQuaternion = HsQuaternion.makeIdentityQuaternion();
  static readonly #quaternionTemp3: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  /**
   * `_accumulatedLeftMouseDelta` is ignored: drags read the delta their action gathered since the last frame. It
   * stays so existing callers keep compiling.
   */
  constructor(
    private readonly camera: HsCamera,
    private readonly controlActionsMap: Map<HsControlActionType, HsControlAction>,
    private readonly canvasSize: HsCanvasSize,
    _accumulatedLeftMouseDelta?: HsVector2
  ) {}

  public processControlActions(): void {
    this.frameStartTime = this.frameEndTime;
    this.frameEndTime = Date.now();
    const elapsedTime: number = this.frameStartTime === null ? this.deltaTime : this.frameEndTime - this.frameStartTime;
    // Prevent a paused tab or delayed first frame from turning one key event into a teleport.
    this.deltaTime = Math.min(Math.max(elapsedTime, 0), MAX_DELTA_TIME_MS);

    const controlActions: IterableIterator<HsControlAction> = this.controlActionsMap.values();
    for (let index: number = 0; index < this.controlActionsMap.size; index++) {
      const element: IteratorResult<HsControlAction> = controlActions.next();
      const controlAction: HsControlAction | undefined = element.value;

      if (controlAction === undefined || !controlAction.isActive) {
        continue;
      }

      if (controlAction instanceof HsScrollControlAction) {
        this.processScrollControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsZoomControlAction) {
        this.processZoomControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsMouseMoveControlAction) {
        this.processMoveControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsKeyboardControlAction) {
        this.processKeyboardControlAction(controlAction);
        continue;
      }
    }
  }

  private processScrollControlAction(scrollActon: HsScrollControlAction): void {
    const { position: positionOnScreen, scrollDelta }: HsScrollControlAction = scrollActon;
    this.moveAlongScreenRay(positionOnScreen, -scrollDelta * this.speed * SPEED_ROTATE * SCROLL_DISTANCE_FACTOR);
  }

  private processZoomControlAction(zoomAction: HsZoomControlAction): void {
    const { position, scale, pivot, fallbackDistance, minDistance }: HsZoomControlAction = zoomAction;
    const direction: HsVector3 = this.getScreenRayDirection(position, HsCameraDefaultMove.#vector3Temp2);
    let distance: number = fallbackDistance;
    if (pivot !== null) {
      // Distance along the ray, so a pivot that drifted off the ray while the fingers panned stays usable.
      const cameraPosition: HsVector3 = HsCameraDefaultMove.#vector3Temp1.setFromMatrixPosition(
        this.camera.worldMatrix.values
      );
      distance = HsVector3.dot(HsCameraDefaultMove.#vector3Temp3.copy(pivot).subtract(cameraPosition), direction);
    }

    this.moveAlongScreenRay(position, hsComputeZoomTravel(distance, scale, minDistance));
  }

  private moveAlongScreenRay(positionOnScreen: HsVector2, travel: number): void {
    const cameraPosition: HsVector3 = HsCameraDefaultMove.#vector3Temp1.setFromMatrixPosition(
      this.camera.worldMatrix.values
    );
    const direction: HsVector3 = this.getScreenRayDirection(positionOnScreen, HsCameraDefaultMove.#vector3Temp2);

    direction.multiplyByScalar(travel);
    cameraPosition.add(direction);
    HsCameraDefaultMove.#matrix4Temp1.copy(this.camera.worldMatrix).setPosition(cameraPosition.values);
    hsApplyTransformationToCamera(this.camera, HsCameraDefaultMove.#matrix4Temp1);
  }

  private getScreenRayDirection(positionOnScreen: HsVector2, result: HsVector3): HsVector3 {
    const { worldMatrix, projectionMatrixInverse }: HsCamera = this.camera;

    const canvasSize: HsCanvasSize = this.canvasSize;
    const deviceCoordinateX: number = ((positionOnScreen.x - canvasSize.left) / canvasSize.renderWidth) * 2 - 1;
    const deviceCoordinateY: number = 1 - ((positionOnScreen.y - canvasSize.top) / canvasSize.renderHeight) * 2;

    const cameraPosition: HsVector3 = HsCameraDefaultMove.#vector3Temp3.setFromMatrixPosition(worldMatrix.values);
    return result
      .set(deviceCoordinateX, deviceCoordinateY, 0.5)
      .unproject(projectionMatrixInverse.values, worldMatrix.values)
      .subtract(cameraPosition)
      .normalize();
  }

  private processMoveControlAction(moveAction: HsMouseMoveControlAction): void {
    const { delta }: HsMouseMoveControlAction = moveAction;
    switch (this.dragNavigation[moveAction.actionType]) {
      case 'look':
        this.lookAround(delta);
        return;
      case 'orbit':
        if (this.orbitPivot === null) {
          this.lookAround(delta);
        } else {
          this.orbit(this.orbitPivot, delta);
        }
        return;
      case 'pan':
        this.pan(delta);
        return;
      default:
        return;
    }
  }

  /** Turns the camera around the pivot; dragging right turns the model to the right, as if grabbing it. */
  private orbit(pivot: HsVector3, delta: HsVector2): void {
    const coefficient: number = ORBIT_DEGREES_PER_PIXEL * HsHypersceneConstants.DEG_2_RAD * this.getRenderCanvasRatio();
    const yaw: number = delta.x * coefficient;
    const pitch: number = delta.y * coefficient;
    if (yaw === 0 && pitch === 0) {
      return;
    }
    HsCameraDefaultMove.#matrix4Temp1.setFromArray(
      hsOrbitCameraMatrix(this.camera.worldMatrix.values, pivot.values, yaw, pitch)
    );
    hsApplyTransformationToCamera(this.camera, HsCameraDefaultMove.#matrix4Temp1);
  }

  /** Turns the camera in place: yaw around the world up axis and pitch within the camera angle limits. */
  private lookAround(delta: HsVector2): void {
    const cameraMatrix: HsMatrix4 = this.camera.worldMatrix;
    const cameraQuaternion: HsQuaternion = HsCameraDefaultMove.#quaternionTemp1.setFromRotationMatrix(
      cameraMatrix.values
    );

    const verticalQuaternion: HsQuaternion = HsCameraDefaultMove.#quaternionTemp2
      .set(cameraQuaternion.x, 0, 0, cameraQuaternion.w)
      .normalize();

    const horizontalQuaternion: HsQuaternion = HsCameraDefaultMove.#quaternionTemp3
      .set(0, cameraQuaternion.y, 0, cameraQuaternion.w)
      .normalize();

    const renderCanvasRatio: number = this.getRenderCanvasRatio();

    const coefficient: number = SPEED_MOVE * HsHypersceneConstants.DEG_2_RAD * renderCanvasRatio;

    const rotationDeltaAroundX: number = delta.y * coefficient;

    const axisX: HsVector3 = HsCameraDefaultMove.#vector3Temp1.set(1, 0, 0);
    const deltaXQuaternion: HsQuaternion = HsCameraDefaultMove.#quaternionTemp1.setFromAxisAngle(
      axisX.values,
      rotationDeltaAroundX
    );

    verticalQuaternion.multiply(deltaXQuaternion);

    const eulerAngles: HsVector3 = HsHypersceneMathUtils.convertToEulerAngles(
      verticalQuaternion,
      HsCameraDefaultMove.#vector3Temp2
    );
    const xAngleToCheck: number = eulerAngles.x;
    if (xAngleToCheck > HsToolsConstants.MAX_CAMERA_ANGLE) {
      verticalQuaternion.setFromAxisAngle(axisX.values, HsToolsConstants.MAX_CAMERA_ANGLE);
    }
    if (xAngleToCheck < HsToolsConstants.MIN_CAMERA_ANGLE) {
      verticalQuaternion.setFromAxisAngle(axisX.values, HsToolsConstants.MIN_CAMERA_ANGLE);
    }

    const rotationDeltaAroundY: number = delta.x * coefficient;

    const axisY: HsVector3 = HsCameraDefaultMove.#vector3Temp1.set(0, 1, 0);
    const deltaYQuaternion: HsQuaternion = HsCameraDefaultMove.#quaternionTemp1.setFromAxisAngle(
      axisY.values,
      rotationDeltaAroundY
    );
    horizontalQuaternion.multiply(deltaYQuaternion);

    const aggregatedQuaternion: HsQuaternion = horizontalQuaternion.multiply(verticalQuaternion);

    const currentPosition: HsVector3 = HsCameraDefaultMove.#vector3Temp1.setFromMatrixPosition(cameraMatrix.values);

    const scale: HsVector3 = HsCameraDefaultMove.#vector3Temp2.set(1, 1, 1);
    const aggregatedMatrix: HsMatrix4 = HsCameraDefaultMove.#matrix4Temp1.compose(
      currentPosition.values,
      aggregatedQuaternion.values,
      scale.values
    );

    hsApplyTransformationToCamera(this.camera, aggregatedMatrix);
  }

  /** Moves the camera in its own plane; the content follows the cursor at the pan anchor's depth. */
  private pan(delta: HsVector2): void {
    const speedMove: number = this.panUnitsPerPixel();

    const cameraMatrixArray: HsHypersceneTypes.Array16 = this.camera.worldMatrix.values;
    HsCameraDefaultMove.#vector3Temp1.setFromMatrixPosition(cameraMatrixArray);

    const cameraQuaternion: HsQuaternion =
      HsCameraDefaultMove.#quaternionTemp1.setFromRotationMatrix(cameraMatrixArray);
    const cameraRotationMatrix: HsMatrix4 = HsCameraDefaultMove.#matrix4Temp2.compose(
      [0, 0, 0],
      cameraQuaternion.values,
      [1, 1, 1]
    );

    HsCameraDefaultMove.#vector3Temp2.set(1, 0, 0);
    HsCameraDefaultMove.#vector3Temp2.applyMatrix4(cameraRotationMatrix.values);
    HsCameraDefaultMove.#vector3Temp2.normalize();
    HsCameraDefaultMove.#vector3Temp2.multiplyByScalar(delta.x * speedMove);
    HsCameraDefaultMove.#vector3Temp1.add(HsCameraDefaultMove.#vector3Temp2);

    HsCameraDefaultMove.#vector3Temp2.set(0, 1, 0);
    HsCameraDefaultMove.#vector3Temp2.applyMatrix4(cameraRotationMatrix.values);
    HsCameraDefaultMove.#vector3Temp2.normalize();
    HsCameraDefaultMove.#vector3Temp2.multiplyByScalar(-delta.y * speedMove);
    HsCameraDefaultMove.#vector3Temp1.add(HsCameraDefaultMove.#vector3Temp2);

    HsCameraDefaultMove.#matrix4Temp1
      .copy(this.camera.worldMatrix)
      .setPosition(HsCameraDefaultMove.#vector3Temp1.values);
    hsApplyTransformationToCamera(this.camera, HsCameraDefaultMove.#matrix4Temp1);
  }

  /** World units a pan moves per CSS pixel: a pixel's size at the anchor's depth, or the fixed step without one. */
  private panUnitsPerPixel(): number {
    const fixed: number = HsToolsConstants.PAN_UNITS_PER_PIXEL * this.getRenderCanvasRatio();
    const anchor: HsVector3 | null = this.panAnchor;
    const projection: HsHypersceneTypes.Array16 = this.camera.projectionMatrix.values;
    const height: number = this.canvasSize.realHeight;
    if (anchor === null || projection[5] === 0 || height <= 0) {
      return fixed;
    }
    // projection[5] is 1 / tan(fov / 2) for a perspective camera and 2 / (top - bottom) for an orthographic one.
    if (projection[15] === 1) {
      return 2 / (projection[5] * height);
    }
    const world: HsHypersceneTypes.Array16 = this.camera.worldMatrix.values;
    // Depth along the view direction (the camera looks down its local -Z).
    const depth: number = -(
      (anchor.x - world[12]) * world[8] +
      (anchor.y - world[13]) * world[9] +
      (anchor.z - world[14]) * world[10]
    );
    return depth > 0 ? (2 * depth) / (projection[5] * height) : fixed;
  }

  private processKeyboardControlAction(keyboardControlAction: HsKeyboardControlAction): void {
    const delta: HsVector3 = HsCameraDefaultMove.#vector3Temp1.set(0, 0, 0);

    const moveSpeed: number =
      HsToolsConstants.SPEED_FACTOR *
      this.speed *
      keyboardControlAction.movementScale *
      this.deltaTime *
      COEFFICIENT_DELTA_TIME_KEYBOARD;
    switch (keyboardControlAction.actionType) {
      case HsControlActionType.PressedRight:
        delta.set(delta.x + moveSpeed, delta.y, delta.z);
        break;
      case HsControlActionType.PressedTop:
        delta.set(delta.x, delta.y, delta.z - moveSpeed);
        break;
      case HsControlActionType.PressedLeft:
        delta.set(delta.x - moveSpeed, delta.y, delta.z);
        break;
      case HsControlActionType.PressedBottom:
        delta.set(delta.x, delta.y, delta.z + moveSpeed);
        break;
      case HsControlActionType.PressedAdditionalOne:
        delta.set(delta.x, delta.y - moveSpeed, delta.z);
        break;
      case HsControlActionType.PressedAdditionalTwo:
        delta.set(delta.x, delta.y + moveSpeed, delta.z);
        break;
      case HsControlActionType.PressedPlus:
        this.speed += SPEED_CHANGE;
        break;
      case HsControlActionType.PressedMinus:
        this.speed -= SPEED_CHANGE;
        this.speed = this.speed < MIN_SPEED ? MIN_SPEED : this.speed;
        break;
      default:
        return;
    }

    this.moveTo(delta);
  }

  private moveTo(delta: HsVector3): void {
    if (delta.isZero()) {
      return;
    }

    const cameraPosition: HsVector3 = HsCameraDefaultMove.#vector3Temp3.setFromMatrixPosition(
      this.camera.worldMatrix.values
    );
    const cameraQuaternion: HsQuaternion = HsCameraDefaultMove.#quaternionTemp1.setFromRotationMatrix(
      this.camera.worldMatrix.values
    );
    const horizontalQuaternion: HsQuaternion = HsCameraDefaultMove.#quaternionTemp2.set(
      0,
      cameraQuaternion.y,
      0,
      cameraQuaternion.w
    );

    HsCameraDefaultMove.#vector3Temp2
      .set(1, 0, 0)
      .applyQuaternion(horizontalQuaternion.values)
      .multiplyByScalar(delta.x);
    cameraPosition.add(HsCameraDefaultMove.#vector3Temp2);
    HsCameraDefaultMove.#vector3Temp2
      .set(0, 1, 0)
      .applyQuaternion(horizontalQuaternion.values)
      .multiplyByScalar(delta.y);
    cameraPosition.add(HsCameraDefaultMove.#vector3Temp2);
    HsCameraDefaultMove.#vector3Temp2
      .set(0, 0, 1)
      .applyQuaternion(horizontalQuaternion.values)
      .multiplyByScalar(delta.z);
    cameraPosition.add(HsCameraDefaultMove.#vector3Temp2);

    const aggregatedMatrix: HsMatrix4 = HsCameraDefaultMove.#matrix4Temp1.copy(this.camera.worldMatrix);
    aggregatedMatrix.setPosition(cameraPosition.values);
    hsApplyTransformationToCamera(this.camera, aggregatedMatrix);
  }

  private getRenderCanvasRatio(): number {
    const canvasSize: HsCanvasSize = this.canvasSize;
    if (canvasSize.renderWidth < canvasSize.realWidth) {
      return canvasSize.renderWidth / canvasSize.realWidth;
    }

    return 1;
  }
}
